import { AnnaError } from "@anna-money/anna-web-lib/utils";
import { makeObservable, observable } from "mobx";

import { type AnalyticsManager } from "services/analytics/analyticsManager";
import { type CompanyStore } from "services/company/companyStore";
import { type CompanyProductsStore } from "services/companyProducts/companyProductsStore";
import { CompanyProduct } from "services/companyProducts/companyProductsTypes";
import { PricingPlan, type PricingPlanProducts } from "services/pricingPlan/pricingPlanTypes";
import { type PricingPlanClient } from "./pricingPlanClient";

export interface IPricingPlanStore {
  getPricingPlan: () => unknown;
  isProductIncluded: (product: CompanyProduct, pricingPlan?: PricingPlan) => boolean;
}

export abstract class PricingPlanStoreBase<T extends PricingPlan> implements IPricingPlanStore {
  private _pricingPlanProducts?: PricingPlanProducts = undefined;

  protected constructor(
    protected readonly _companyStore: CompanyStore,
    protected readonly _companyProductsStore: CompanyProductsStore,
    protected readonly _pricingPlanClient: PricingPlanClient,
    protected readonly _analyticsManager: AnalyticsManager,
  ) {
    makeObservable(this, {
      _pricingPlanProducts: observable,
    } as any);
  }

  get pricingPlanProducts(): PricingPlanProducts | undefined {
    return this._pricingPlanProducts;
  }

  private set pricingPlanProducts(value) {
    this._pricingPlanProducts = value;
  }

  abstract isSupportedPricingPlan(pricingPlan: PricingPlan): pricingPlan is T;

  async init(): Promise<void> {
    this.pricingPlanProducts = await this._pricingPlanClient.getPricingPlanProducts();
  }

  tryGetPricingPlan(): T | undefined {
    const pricingPlan = this._companyStore.company?.pricingPlan;

    if (!pricingPlan) {
      return undefined;
    }

    if (!this.isSupportedPricingPlan(pricingPlan)) {
      throw new AnnaError(`Pricing plan ${pricingPlan} is not expected`);
    }

    return pricingPlan;
  }

  getPricingPlan(): T {
    const pricingPlan = this.tryGetPricingPlan();

    if (!pricingPlan) {
      throw new AnnaError("No pricing plan selected");
    }

    return pricingPlan;
  }

  async updatePricingPlan(pricingPlan: T): Promise<void> {
    await this._companyStore.updatePricingPlan(pricingPlan);
    this._analyticsManager.event("basket.change-plan", { plan: pricingPlan });
  }

  isProductIncluded(product: CompanyProduct, pricingPlan?: PricingPlan): boolean {
    const plan = pricingPlan || this.tryGetPricingPlan();

    if (!plan) {
      return false;
    }

    return this._getProductsForPlan(plan).includes(product);
  }

  private _getProductsForPlan(pricingPlan: PricingPlan): CompanyProduct[] {
    return this.pricingPlanProducts ? this.pricingPlanProducts[pricingPlan] : [];
  }
}
